import React, { useState, useEffect, useCallback } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Switch,
  Button,
  CircularProgress,
  Checkbox,
  styled,
  TablePagination,
  TableSortLabel,
  IconButton, 
  Box 
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { 
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material';

import { getDeliveriesWithOrders, getDeliveriesWithOrdersWithNextToken, updateDiscountStatus, setPurchasedWithSupplier, setDiscountAtCheckout } from './queries'
import { TerratreeLogs } from '../../types';
import BadSKUModal from '../BadSKUModal';
import TablePaginationActionsProps from './tablePaginationActionProps';
import './styles.css';


const BadSkuButton = styled(Button)({
  backgroundColor: '#008000', // Dark green
  color: 'white',
  textTransform: 'none',
  padding: '6px 16px',
  fontSize: '13px',
  '&:hover': {
    backgroundColor: '#006400',
  },
  borderRadius: '4px',
});

const StyledCheckbox = styled(Checkbox)({
  padding: '4px',
  '&.Mui-checked': {
    color: '#008000',
  }
});

const StyledTablePagination = styled(TablePagination)({
  '.MuiTablePagination-select': {
    paddingLeft: '8px',
    paddingRight: '24px',
  },
  '.MuiTablePagination-selectIcon': {
    right: '0',
  },
  '.MuiTablePagination-displayedRows': {
    color: '#666',
    fontSize: '13px',
  },
  '.MuiTablePagination-selectLabel': {
    color: '#666',
    fontSize: '13px',
  }
});


const StyledTableContainer = styled(TableContainer)({
  width: '100%',
  '& .MuiTable-root': {
    minWidth: 'auto', // Remove any minimum width
    width: '100%',
  },
  '& .MuiTableCell-root': {
    padding: '8px 4px', // Reduce padding
    fontSize: '13px',
    whiteSpace: 'nowrap',
  },
  '& .MuiButton-root': {
    padding: '4px 8px', // Make buttons smaller
    minWidth: 'auto',
  },
  '& .MuiCheckbox-root': {
    padding: '4px', // Make checkboxes smaller
  }
});

const StyledTableCell = styled(TableCell)({
  padding: '8px 4px', // Reduced padding
  fontSize: '12px', // Slightly smaller font
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  borderBottom: 'none',
  color: '#555',
  whiteSpace: 'nowrap',
  '&.MuiTableCell-head': {
    fontWeight: 500,
    color: '#666',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e0e0e0',
  },
  // Add max-width and text overflow for specific columns
  '&.column-id': {
    maxWidth: '130px',
    overflow: 'hidden',
    paddingRight: '5px',
    textOverflow: 'ellipsis',
  },
  '&.column-total-earnings': {
    maxWidth: '120px',
    overflow: 'hidden',
    paddingLeft: '20px',
    textOverflow: 'ellipsis',
  },
  '&.column-sku': {
    maxWidth: '120px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '&.column-asin': {
    maxWidth: '100px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '&.column-date': {
    width: '90px',
  },
  '&.column-status': {
    width: '90px',
  },
  '&.column-type': {
    width: '90px',
  },
  '&.column-replacement': {
    width: '100px',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: '1.2'
  },
  '&.column-service': {
    width: '100px',
  },
  '&.column-business': {
    width: '80px',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: '1.2'
  },
  '&.column-supplier': {
    width: '60px',
    padding: '0 4px',
  },
  '&.column-discount': {
    width: '60px',
    padding: '0 4px',
  },
  '&.column-badsku': {
    width: '100px',
  }
});

const StyledTableRow = styled(TableRow)({
  '&.highlight-green': {
    backgroundColor: 'rgba(40, 167, 69, 0.1)',
    borderLeft: '3px solid rgba(40, 167, 69, 0.6)',
  },
  '&.highlight-blue': {
    backgroundColor: 'rgba(0, 123, 255, 0.08)', 
    borderLeft: '3px solid rgba(0, 123, 255, 0.6)', 
  },
  '&.highlight-red': {
    backgroundColor: 'rgba(220, 53, 69, 0.1)',
    borderLeft: '3px solid rgba(220, 53, 69, 0.6)', 
  },
  '&:hover': {
    cursor: 'pointer',
  },
  '&.highlight-green:hover': {
    backgroundColor: 'rgba(40, 167, 69, 0.18) !important',
  },
  '&.highlight-blue:hover': {
    backgroundColor: 'rgba(0, 123, 255, 0.15) !important',
  },
  '&.highlight-red:hover': {
    backgroundColor: 'rgba(220, 53, 69, 0.18) !important',
  },
  '& td:first-of-type': {
    borderTopLeftRadius: '4px',
    borderBottomLeftRadius: '4px',
  },
  '& td:last-of-type': {
    borderTopRightRadius: '4px',
    borderBottomRightRadius: '4px',
  },

  '&.copying': {
    userSelect: 'none',
    backgroundColor: 'rgba(255, 193, 7, 0.15) !important', 
    borderLeft: '3px solid rgba(255, 193, 7, 0.8)',
  }
});

type Order = 'asc' | 'desc';
type SortableColumn = 'amazonOrderId' | 'purchaseDate' | 'purchasedWithSupplier' | 'discountAtCheckout' | 'badSKU' | 'totalEarnings';


function TablePaginationActions(props: TablePaginationActionsProps) {
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        <FirstPageIcon />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        <LastPageIcon />
      </IconButton>
    </Box>
  );
}

const TerratreeTable: React.FC = () => {
    const [logs, setLogs] = useState<TerratreeLogs[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isTrackingFilter, setIsTrackingFilter] = useState<boolean>(false);
    const [filterValue, setFilterValue] = useState<string>('');
    const [selectedRow, setSelectedRow] = useState<TerratreeLogs | null>(null);
    const [showBadSKUModal, setShowBadSKUModal] = useState<boolean>(false);
    const totalEarningsMap: Record<string, number> = {};
    const cogsMap: Record<string, string> = {};
    const [isCopying, setIsCopying] = useState<string | null>(null); 
    

    const getData = useCallback(async () => {
      setIsLoading(true);
      try {
       
        let response: any = await API.graphql(graphqlOperation(getDeliveriesWithOrders));
        let items = response.data.listDeliverys.items;
        let allItems = [...items];

        while (response.data.listDeliverys.nextToken) {
          response = await API.graphql(
            graphqlOperation(getDeliveriesWithOrdersWithNextToken, {
              nextToken: response.data.listDeliverys.nextToken,
            })
          );
          allItems = [...allItems, ...response.data.listDeliverys.items];
        }


        try {
          const lambdaFunctionUrl = "https://g74wutcrxs5hrpgejmovdrztg40dorma.lambda-url.us-east-1.on.aws/";
          const response = await fetch(lambdaFunctionUrl, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
          });
        
          if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        
          const result = await response.json();
        
          let bodyData;
          if (typeof result.body === 'string') {
            bodyData = JSON.parse(result.body);
          } else if (result.body) {
            bodyData = result.body;
          } else if (result.items) {
            bodyData = { items: result.items };
          } else {
            bodyData = result;
          }
        
          const testTableData = bodyData.items || [];
        
          testTableData.forEach((item: any) => {
            if (item && item.AmazonOrderId) {
              totalEarningsMap[item.AmazonOrderId] = Number(parseMoney(item.TotalEarnings).replace(/[^0-9.-]+/g, ''));
              if (item.COGS) {
                cogsMap[item.AmazonOrderId] = item.COGS;
              }
            }
          });

        } catch (err) {
          console.error("Error fetching Lambda TEST_TABLE data:", err);
        }


        const formattedLogs: TerratreeLogs[] = allItems
        .filter((item: any) => {
          // Filter out items with invalid dates
          return (
            item.PurchaseDate && 
            item.PurchaseDate !== ''
          );
        })
        .map((item: any) => ({
          amazonOrderId: item.AmazonOrderId || '',
          orderStatus: item.OrderStatus || '',
          orderType: item.OrderType || '',
          isReplacementOrder: Boolean(item.IsReplacementOrder),
          shipServiceLevel: item.ShipServiceLevel || '',
          purchaseDate: item.PurchaseDate ? new Date(item.PurchaseDate) : new Date(),
          latestShipDate: item.LatestShipDate ? new Date(item.LatestShipDate) : undefined,
          isBusinessOrder: Boolean(item.IsBusinessOrder),
          sellerSKU: Array.isArray(item.OrderItems) && item.OrderItems.length > 0 
            ? item.OrderItems.map((orderItem: any) => orderItem.SellerSKU || '').filter(Boolean)
            : (Array.isArray(item.SellerSKU) ? item.SellerSKU : (item.SellerSKU ? item.SellerSKU.replace(/[$$$$"']/g, "").split(",").map((s: string) => s.trim()) : [])),
          ASIN: Array.isArray(item.OrderItems) && item.OrderItems.length > 0
            ? item.OrderItems.map((orderItem: any) => orderItem.ASIN || '').filter(Boolean)
            : (Array.isArray(item.ASIN) ? item.ASIN : (item.ASIN ? item.ASIN.replace(/[$$$$"']/g, "").split(",").map((s: string) => s.trim()) : [])),
          trackingNumber: item.DeliveryID || '',
          purchasedWithSupplier: Boolean(item.PurchasedWithSupplier),
          discountAtCheckout: Boolean(item.discountAtCheckout),
          badSKU: item.BadSKU || null,
          orderItems: Array.isArray(item.OrderItems) ? item.OrderItems.map((orderItem: any) => ({
            OrderItemId: orderItem.OrderItemId || '',
            SellerSKU: orderItem.SellerSKU || '',
            ASIN: orderItem.ASIN || '',
            BadSku: Boolean(orderItem.BadSku),
            QuantityOrdered: Number(orderItem.QuantityOrdered) || 0,
            QuantityShipped: Number(orderItem.QuantityShipped) || 0,
            TrackingId: orderItem.TrackingId || '',
            ShippingProvider: orderItem.ShippingProvider || '',
            ShippingType: orderItem.ShippingType || ''
          })) : [],
          totalEarnings: totalEarningsMap[item.AmazonOrderId] !== undefined
          ? totalEarningsMap[item.AmazonOrderId]
          : parseMoney(item.TotalEarnings),
        }));

        setLogs(formattedLogs.sort((a, b) => a.purchaseDate.getTime() - b.purchaseDate.getTime()));

        // Log any errors that occurred
        if (response.errors) {
          console.warn('Some items were skipped due to data validation:', response.errors);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setIsLoading(false);
    }, []);

    useEffect(() => {
      getData();
      const interval = setInterval(getData, 3600000); // Refresh every hour
      return () => clearInterval(interval);
    }, [getData]);


    const handleFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value; // Remove trim() and toLowerCase()
      setFilterValue(value);
      setPage(0);
    };
  
    const handleTrackingFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsTrackingFilter(event.target.checked);
      setPage(0);
    };
  
    const handleRefresh = () => {
      getData();
    };
  
    const handleSetShippingOptions = (row: TerratreeLogs, event?: React.MouseEvent) => {
     
      if (isCopying === row.amazonOrderId) {
        return;
      }
      
      const target = event?.target as HTMLElement;
      if (target && (
        target.tagName === 'BUTTON' ||
        target.tagName === 'INPUT' ||
        target.closest('button') ||
        target.closest('.MuiCheckbox-root') ||
        target.closest('.MuiIconButton-root')
      )) {
        return;
      }

      setSelectedRow(row);
    };

    const handleCopyOperation = async (text: string, orderId: string) => {
      setIsCopying(orderId);
      
      try {
        await navigator.clipboard.writeText(text);
        console.log('Copied to clipboard:', text);
      } catch (err) {
        console.error('Failed to copy text: ', err);
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setTimeout(() => {
        setIsCopying(null);
      }, 300);
    };

    
  
    const handleSetCheck = async (checked: boolean, row: TerratreeLogs) => {
      try {
        const purchaseDate = 
          typeof row.purchaseDate === 'string' ? 
            row.purchaseDate : new Date(row.purchaseDate).toISOString().slice(0, 19) + 'Z';
        
        await API.graphql(graphqlOperation(setPurchasedWithSupplier, { 
          amazonOrderId: row.amazonOrderId,
          purchaseDate: purchaseDate,
          purchased: checked
        }));
        
        // Update local state
        setLogs(prevLogs => 
          prevLogs.map(log => 
            log.amazonOrderId === row.amazonOrderId 
              ? { ...log, purchasedWithSupplier: checked }
              : log
          )
        );
      } catch (error) {
        console.error('Error updating purchase with supplier:', error);
        alert('Failed to update "Purchased with Supplier": ' + JSON.stringify(error));
      }
    };
  
    const handleSetBadSKU = (row: TerratreeLogs) => {
      setSelectedRow(row);
      setShowBadSKUModal(true);
    };
  
    const handleSetDiscountStatus = async (checked: boolean, row: TerratreeLogs) => {
      try {
        const purchaseDate =
          typeof row.purchaseDate=== 'string'
              ? row.purchaseDate
              : new Date(row.purchaseDate).toISOString().slice(0, 19) + 'Z';

        await API.graphql(graphqlOperation(setDiscountAtCheckout, { 
          amazonOrderId: row.amazonOrderId,
          purchaseDate: purchaseDate,
          discount: checked
        }));
        
        setLogs(prevLogs => 
          prevLogs.map(log => 
            log.amazonOrderId === row.amazonOrderId 
              ? { ...log, discountAtCheckout: checked }
              : log
          )
        );
      } catch (error) {
        console.error('Error updating discount status:', error);
        alert('Failed to update "Discount at Checkout": ' + JSON.stringify(error));
      }
    };

    const handleRowClick = (row: TerratreeLogs, event: React.MouseEvent) => {
      if (isCopying || event.defaultPrevented) {
        return;
      }
      handleSetShippingOptions(row, event);
    };
  
    const getRowClassName = (row: TerratreeLogs): string => {
      let className = '';
      
      if (isCopying === row.amazonOrderId) {
        className += 'copying ';
      }
      
      // Debug logging
      if (row.amazonOrderId === '113-6241572-5855455' || row.amazonOrderId === '112-1412687-9047457') {
        console.log(`Order ${row.amazonOrderId} badSKU:`, row.badSKU, typeof row.badSKU, Boolean(row.badSKU));
      }
      
      if (row.badSKU) {
        className += 'highlight-red';
      } else if (row.purchasedWithSupplier) {
        className += 'highlight-green';
      } else {
        className += 'highlight-blue';
      }
      
      return className.trim();
    };
  
    const filteredLogs = logs.filter(log => {
      if (isTrackingFilter && log.trackingNumber) return false;
      
      if (!filterValue) return true;
    
      const searchValue = filterValue.toLowerCase();
      
      // Properly handle SKU and ASIN arrays
      const skuString = log.sellerSKU.join(' ').toLowerCase();
      const asinString = log.ASIN.join(' ').toLowerCase();
      
      return (
        log.amazonOrderId.toLowerCase().includes(searchValue) ||
        log.orderStatus.toLowerCase().includes(searchValue) ||
        log.orderType.toLowerCase().includes(searchValue) ||
        skuString.includes(searchValue) || 
        asinString.includes(searchValue)
      );
    });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const [orderBy, setOrderBy] = useState<SortableColumn>('purchaseDate');
    const [order, setOrder] = useState<Order>('desc');

    const handleRequestSort = (property: SortableColumn) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    };

    const sortData = (data: TerratreeLogs[], orderBy: SortableColumn, order: Order) => {
      return [...data].sort((a, b) => {
        let aValue: any = a[orderBy];
        let bValue: any = b[orderBy];
        
        if (orderBy === 'purchaseDate') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        
        if (typeof aValue === 'boolean') {
          aValue = aValue ? 1 : 0;
          bValue = bValue ? 1 : 0;
        }
        
        if (orderBy === 'badSKU') {
          aValue = a.badSKU ? 1 : 0;
          bValue = b.badSKU ? 1 : 0;
        }
        
        if (order === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
        }
      });
    };

    const sortedLogs = sortData(filteredLogs, orderBy, order);

    const handleChangePage = (event: unknown, newPage: number) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    };
  
    const handleSuccess = useCallback(() => {
        getData();
        setShowBadSKUModal(false);
    }, [getData]);

    const parseMoney = (value: any): string => {
      if (value === null || value === undefined) return '0.00';
      
      let numericValue: number;
    
      if (typeof value === 'number') {
        numericValue = value;
      } else if (typeof value === 'string') {
        const cleanedValue = value.replace(/[^0-9.-]/g, '');
        numericValue = parseFloat(cleanedValue) || 0;
      } else {
        numericValue = parseFloat(String(value)) || 0;
      }
    
      return numericValue.toFixed(2); // Always returns string with 2 decimals
    };

    const SortableTableCell = ({ 
      id, 
      label, 
      className 
    }: { 
      id: SortableColumn, 
      label: string, 
      className: string 
    }) => (
      <StyledTableCell className={className}>
        <TableSortLabel
          active={orderBy === id}
          direction={orderBy === id ? order : 'asc'}
          onClick={() => handleRequestSort(id)}
        >
          {label}
        </TableSortLabel>
      </StyledTableCell>
    );


    return (
      <div className="table-container">
        <div className="table-buttons">
          <TextField
            label="Filter"
            variant="outlined"
            className="filter-input"
            onChange={handleFilter}
            value={filterValue}
          />
          <div className="tracking-toggle">
            <Switch
              checked={isTrackingFilter}
              onChange={handleTrackingFilter}
              color="primary"
            />
            <span>Empty Tracking Numbers Only</span>
          </div>
          <Button
            variant="contained"
            className="refresh-button"
            onClick={handleRefresh}
            disabled={isLoading}
            startIcon={<RefreshIcon />}
            sx={{
              backgroundColor: '#008000', // Same green as Set Bad SKU
              color: 'white',
              '&:hover': {
                backgroundColor: '#006400', // Darker green on hover
              },
              '&:disabled': {
                backgroundColor: '#cccccc', // Grey when disabled
              }
            }}
          >
            Refresh
          </Button>
        </div>
  
        {isLoading ? (
          <div className="spinner">
            <CircularProgress />
          </div>
        ) : (
          <StyledTableContainer>
            <Paper elevation={0}>
            <Table  style={{ width: '100%' }}>
              <TableHead>
                <TableRow>
                  <SortableTableCell id="amazonOrderId" label="Amazon Order ID" className="column-id" />
                  <SortableTableCell id="totalEarnings" label="Total Earnings" className="column-total-earnings" />
                  <StyledTableCell className="column-sku">Seller SKU</StyledTableCell>
                  <StyledTableCell className="column-asin">ASIN</StyledTableCell>
                  <SortableTableCell id="purchaseDate" label="Purchase Date" className="column-date" />
                  <StyledTableCell className="column-date">Ship By</StyledTableCell>
                  <StyledTableCell className="column-status">Order Status</StyledTableCell>
                  <StyledTableCell className="column-replacement">Replacement Order</StyledTableCell>
                  <StyledTableCell className="column-business">Business Order</StyledTableCell>
                  <StyledTableCell 
                    className="column-supplier"
                    style={{ 
                      width: '120px',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      lineHeight: '1.2'
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'purchasedWithSupplier'}
                      direction={orderBy === 'purchasedWithSupplier' ? order : 'asc'}
                      onClick={() => handleRequestSort('purchasedWithSupplier')}
                    >
                      Purchased with Supplier
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell 
            className="column-discount"
            style={{ 
              width: '120px',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: '1.2'
            }}
          >
            <TableSortLabel
              active={orderBy === 'discountAtCheckout'}
              direction={orderBy === 'discountAtCheckout' ? order : 'asc'}
              onClick={() => handleRequestSort('discountAtCheckout')}
            >
              Discount at Checkout
            </TableSortLabel>
          </StyledTableCell>
                  <SortableTableCell id="badSKU" label="Bad SKU?" className="column-badsku" />
                </TableRow>
              </TableHead>
              <TableBody>
              {sortedLogs
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <StyledTableRow  
                    key={row.amazonOrderId}
                    className={getRowClassName(row)}
                    onClick={(event) => handleRowClick(row, event)}
                  >
                    <StyledTableCell 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyOperation(row.amazonOrderId, row.amazonOrderId);
                        }}
                        style={{ 
                          cursor: 'copy',
                          position: 'relative'
                        }}
                        title="Click to copy Order ID"
                      >
                        {row.amazonOrderId}
                        {isCopying === row.amazonOrderId && (
                          <span style={{
                            position: 'absolute',
                            right: '4px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: '10px',
                            color: '#007bff',
                            fontWeight: 'bold'
                          }}>
                            ✓
                          </span>
                        )}
                    </StyledTableCell>
                    <StyledTableCell style={{ paddingLeft: '20px' }}>${row.totalEarnings}</StyledTableCell>
                    <StyledTableCell
                        onClick={(e) => {
                          e.stopPropagation();
                          const skuText = row.sellerSKU.map(sku => sku.trim().replace(/[\[\]]/g, '')).join(', ');
                          handleCopyOperation(skuText, row.amazonOrderId);
                        }}
                        style={{ cursor: 'copy' }}
                        title="Click to copy SKU(s)"
                      >
                        {row.sellerSKU.length > 1 ? (
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {row.sellerSKU.map((sku, index) => (
                              <div key={index}>{sku.trim().replace(/[\[\]]/g, '')}</div>
                            ))}
                          </div>
                        ) : (
                          row.sellerSKU[0]?.replace(/[\[\]]/g, '') || ''
                        )}
                    </StyledTableCell>
                    <StyledTableCell
                        onClick={(e) => {
                          e.stopPropagation();
                          const asinText = row.ASIN.map(asin => asin.trim().replace(/[\[\]]/g, '')).join(', ');
                          handleCopyOperation(asinText, row.amazonOrderId);
                        }}
                        style={{ cursor: 'copy' }}
                        title="Click to copy ASIN(s)"
                      >
                        {row.ASIN.length > 1 ? (
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {row.ASIN.map((asin, index) => (
                              <div key={index}>{asin.trim().replace(/[\[\]]/g, '')}</div>
                            ))}
                          </div>
                        ) : (
                          row.ASIN[0]?.replace(/[\[\]]/g, '') || ''
                        )}
                      </StyledTableCell>
                    <StyledTableCell>{new Date(row.purchaseDate).toLocaleDateString()}</StyledTableCell>
                    <StyledTableCell>{row.latestShipDate ? new Date(row.latestShipDate).toLocaleDateString() : ''}</StyledTableCell>
                    <StyledTableCell>{row.orderStatus}</StyledTableCell>
                    <StyledTableCell>{row.isReplacementOrder ? 'Yes' : 'No'}</StyledTableCell>
                    <StyledTableCell>{row.isBusinessOrder ? 'Yes' : 'No'}</StyledTableCell>
                    <StyledTableCell>
                    <Checkbox
                    checked={row.purchasedWithSupplier}
                    onChange={(e) => handleSetCheck(e.target.checked, row)}
                    disabled={Boolean(row.purchasedWithSupplier || row.badSKU)}
                    onClick={(e) => e.stopPropagation()}
                  />
                    </StyledTableCell>
                    <StyledTableCell>
                    <Checkbox
                    checked={row.discountAtCheckout}
                    onChange={(e) => handleSetDiscountStatus(e.target.checked, row)}
                    disabled={Boolean(row.badSKU)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  
                    </StyledTableCell>
                    <StyledTableCell>
                      <Button
                        variant="contained"
                        color={row.badSKU ? "error" : "success"}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSetBadSKU(row);
                        }}
                      >
                        {row.badSKU ? 'Bad SKU' : 'Set Bad SKU'}
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow >
                ))}
              </TableBody>
             
            </Table>
            <TablePagination
                component="div"
                count={filteredLogs.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 15, 25, 50, 100]}
                labelRowsPerPage="Items per page:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from} – ${to} of ${count}`
                }
                ActionsComponent={TablePaginationActions}
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  borderTop: '1px solid #e0e0e0',
                  paddingTop: '8px',
                  paddingBottom: '8px',
                  '.MuiTablePagination-toolbar': {
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    paddingLeft: 0,
                    paddingRight: '16px',
                    minHeight: '52px',
                  },
                  '.MuiTablePagination-spacer': {
                    flex: '1 1 100%',
                  },
                  '.MuiTablePagination-selectLabel': {
                    color: '#666',
                    fontSize: '13px',
                    marginRight: '8px',
                  },
                  '.MuiTablePagination-select': {
                    paddingLeft: '8px',
                    paddingRight: '24px',
                    marginRight: '16px',
                  },
                  '.MuiTablePagination-selectIcon': {
                    right: '0',
                  },
                  '.MuiTablePagination-displayedRows': {
                    color: '#666',
                    fontSize: '13px',
                    marginRight: '16px',
                  },
                  '.MuiTablePagination-actions': {
                    marginLeft: '8px',
                  }
                }}
              />
            </Paper>
          </StyledTableContainer>
         
        )}
  
        {selectedRow && (
          <>
          <BadSKUModal
              open={showBadSKUModal}
              onClose={() => setShowBadSKUModal(false)}
              data={{ item: selectedRow, badSKU: selectedRow.badSKU }}
              onSuccess={handleSuccess}
          />
          </>
        )}
      </div>
    );
  };
  
  export default TerratreeTable;